import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { useParams, Link } from "react-router";
import { Dropdown } from "react-bootstrap";

import { setItemToStorage } from "../../../../system";
import {
  FILTER_LABEL,
  pathConstant,
  loginConstants,
  groupPerformanceConstants,
  averageCardTitleConstants,
  EMPTY_ARRAY_LENGTH,
  PAGINATION_INDEXING,
  SINGLE_ARRAY_LENGTH,
  GROUP_PERFORMANCE_LABELS,
} from "../../constants";
import {
  brandPerformanceConstants,
  brandProfileConstants,
} from "../../../shared-screens/constants";
import {
  Spinner,
  DataTable,
  StatusCard,
  useSpinner,
  SearchComponent,
  ChangeComponent,
  AverageRatingCard,
  calculateChangeColumnValueForSentimentRating,
  calculateChangeColumnValueForTransactionRating,
} from "../../../../shared";
import { createGroupPerformanceColumns } from "./utility";

const { ALL_GROUP_RESPONSE_DATA } = groupPerformanceConstants;
const { GET_AVERAGE_RATING_RESPONSES, GET_RECORD_PAGINATION_RESPONSES } =
  brandPerformanceConstants;
const { AVERAGE_TRANSACTION_RATING, AVERAGE_SENTIMENT_RATING } =
  averageCardTitleConstants;

//Displaying the general data of a brand and its survey responses in charts
const Performance = (props) => {
  const {
    getData,
    sorting,
    dayCounts,
    exportFunc,
    searchString,
    setSearchString,
    selectedOption,
    totalResponses,
    totalRestaurants,
    userResponsesData,
    userResponseDataPagination,
    avgSentimentAndTransactionRatingData,
    openExportCustomerDataConfirmationModal,
  } = props;

  const { groupId } = useParams();

  // Show loading while a request is running.
  const spinner = useSpinner();

  //Handle store name click
  const handleClickStoreName = (restaurantData) => {
    setItemToStorage(loginConstants.LOCATION_ID, restaurantData.storeId);
    setItemToStorage(
      brandProfileConstants.LOCATION_STATE,
      JSON.stringify({ name: pathConstant.GROUP_PERFROMANCE }),
    );
  };

  useEffect(() => {
    if (userResponsesData && userResponsesData.length > EMPTY_ARRAY_LENGTH) {
      calculateChangeColumnValueForTransactionRating(userResponsesData);
      calculateChangeColumnValueForSentimentRating(userResponsesData);
    }
    // eslint-disable-next-line
  }, [userResponsesData]);

  const cellNameRenderer = useCallback(
    ({ row }) => {
      const restaurantData = row.original;
      const restaurantName = restaurantData.name;
      if (restaurantName) {
        return (
          <Link
            className="table-links"
            to={`/dashboard/${groupId}/group-performance/store-performance/${
              restaurantData.storeId || restaurantData.id
            }`}
            onClick={() => handleClickStoreName(restaurantData)}
            state={{ name: pathConstant.GROUP_PERFROMANCE }}
          >
            {restaurantName}
          </Link>
        );
      } else {
        return <span />;
      }
    },
    // eslint-disable-next-line
    [groupId],
  );

  const headerTransactionRatingRenderer = useCallback(
    () => {
      return (
        <span className="text-secondary">
          {GROUP_PERFORMANCE_LABELS.AVERAGE_TRANSACTION_RATING}
        </span>
      );
    },
    // eslint-disable-next-line
    [],
  );

  const headerSurveyCompletionDateRenderer = useCallback(
    () => {
      return (
        <span className="text-secondary">
          {GROUP_PERFORMANCE_LABELS.PREVIOUS}{" "}
          {dayCounts === SINGLE_ARRAY_LENGTH
            ? `${dayCounts} ${GROUP_PERFORMANCE_LABELS.DAY}`
            : `${dayCounts} ${GROUP_PERFORMANCE_LABELS.DAYS}`}
        </span>
      );
    },
    // eslint-disable-next-line
    [dayCounts],
  );

  const headerSentimentRatingRenderer = useCallback(
    () => {
      return (
        <span className="text-secondary">
          {GROUP_PERFORMANCE_LABELS.AVERAGE_SENTIMENT_RATING}
        </span>
      );
    },
    // eslint-disable-next-line
    [],
  );

  const cellTransactionChangeRenderer = useCallback(
    ({ row }) => {
      const {
        transactionChange,
        isTransactionChangeEqualToZero,
        isTransactionChangeGreaterThanZero,
      } = row.original;
      return (
        <ChangeComponent
          result={transactionChange}
          isValueEqualToZero={isTransactionChangeEqualToZero}
          isValueGreaterThanZero={isTransactionChangeGreaterThanZero}
        />
      );
    },
    // eslint-disable-next-line
    [],
  );

  const cellSentimentChangeRenderer = useCallback(
    ({ row }) => {
      const {
        sentimentChange,
        isSentimentChangeEqualToZero,
        isSentimentChangeGreaterThanZero,
      } = row.original;
      return (
        <ChangeComponent
          result={sentimentChange}
          isValueEqualToZero={isSentimentChangeEqualToZero}
          isValueGreaterThanZero={isSentimentChangeGreaterThanZero}
        />
      );
    },
    // eslint-disable-next-line
    [],
  );

  const Columns = useMemo(
    () =>
      createGroupPerformanceColumns({
        cellNameRenderer,
        headerTransactionRatingRenderer,
        headerSurveyCompletionDateRenderer,
        headerSentimentRatingRenderer,
        cellTransactionChangeRenderer,
        cellSentimentChangeRenderer,
      }),
    [
      cellNameRenderer,
      headerTransactionRatingRenderer,
      headerSurveyCompletionDateRenderer,
      headerSentimentRatingRenderer,
      cellTransactionChangeRenderer,
      cellSentimentChangeRenderer,
    ],
  );

  const showAllResponsesLabel =
    !selectedOption.length ||
    selectedOption.some((item) => item.label === FILTER_LABEL.ALL);

  return (
    <>
      {(spinner(ALL_GROUP_RESPONSE_DATA) ||
        spinner(GET_AVERAGE_RATING_RESPONSES) ||
        spinner(GET_RECORD_PAGINATION_RESPONSES)) && <Spinner />}
      <div className="container-fluid pb-3">
        <div className=" d-md-flex pb-3 justify-content-between">
          <StatusCard
            title={GROUP_PERFORMANCE_LABELS.TOTAL_STORES}
            data={totalRestaurants}
            classNames="me-3 mb-3 mb-md-0"
            bodyClassNames="p-3 mb-0"
          />
          <StatusCard
            title={
              showAllResponsesLabel
                ? GROUP_PERFORMANCE_LABELS.TOTAL_RESPONSES
                : `Total ${selectedOption.map(
                    (item) => item.value,
                  )} Star Responses`
            }
            data={totalResponses}
            classNames=""
            bodyClassNames="p-3 mb-0"
          />
        </div>
        <div className=" d-md-flex mb-3 justify-content-between mt-2">
          <AverageRatingCard
            dayCounts={dayCounts}
            bodyClassNames="px-3 pb-0"
            classNames="me-0 me-md-3 mb-3 mb-md-0"
            title={AVERAGE_TRANSACTION_RATING}
            averageRating={
              avgSentimentAndTransactionRatingData.averageTransactionRatings
            }
            previousAverageRating={
              avgSentimentAndTransactionRatingData.previousAverageTransactionRatings
            }
            averageRatingQuestionsData={
              avgSentimentAndTransactionRatingData.averageTransaction
            }
          />
          <AverageRatingCard
            dayCounts={dayCounts}
            bodyClassNames="px-3 pb-0"
            classNames="mb-md-0 mb-3"
            title={AVERAGE_SENTIMENT_RATING}
            averageRating={
              avgSentimentAndTransactionRatingData.averageSentimentRatings
            }
            previousAverageRating={
              avgSentimentAndTransactionRatingData.previousAverageSentimentRatings
            }
            averageRatingQuestionsData={
              avgSentimentAndTransactionRatingData.averageSentiment
                ? avgSentimentAndTransactionRatingData.averageSentiment
                : []
            }
          />
        </div>
        <div className="pb-3 text-end">
          <Dropdown>
            <Dropdown.Toggle
              id="export-dropdown"
              data-testid="export-button"
              className="green-background-color green-border"
              disabled={!userResponseDataPagination.totalElements}
            >
              {GROUP_PERFORMANCE_LABELS.EXPORT}
            </Dropdown.Toggle>

            <Dropdown.Menu className="text-center shadow">
              <Dropdown.Item onClick={exportFunc}>
                {GROUP_PERFORMANCE_LABELS.DATA_TABLE}
              </Dropdown.Item>
              <Dropdown.Item onClick={openExportCustomerDataConfirmationModal}>
                {GROUP_PERFORMANCE_LABELS.DETAILED_CUSTOMER_SURVEY_DATA}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="notification-form card shadow">
          <div className="card-body px-0">
            <div className="d-flex justify-content-between">
              <h5 className=" responses px-4">
                {GROUP_PERFORMANCE_LABELS.TOTAL_STORES}
              </h5>
              <SearchComponent
                searchString={searchString}
                setSearchString={setSearchString}
              />
            </div>
            <div className="mt-3">
              <DataTable
                columns={Columns}
                data={userResponsesData}
                totalRows={userResponseDataPagination.totalElements}
                numberOfRowsPerPage={userResponseDataPagination.size}
                pageNumber={
                  userResponseDataPagination.number +
                  PAGINATION_INDEXING.PAGE_INDEX_OFFSET
                }
                pageSize={userResponseDataPagination.size}
                sortedBy={sorting}
                onPageChange={(pageNum) =>
                  getData(pageNum, null, userResponseDataPagination.size)
                }
                onColumnSort={(sortBy) =>
                  getData(
                    userResponseDataPagination.number,
                    sortBy,
                    userResponseDataPagination.size,
                  )
                }
                onSizeChange={(size, pageNum) => {
                  getData(pageNum, null, size);
                }}
                isPaginationDropDownEnable={true}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default connect(null, null)(Performance);
